/**
 * Quran Controls Component
 * Two-row top bar with page info/picker, surah dropdown, font-size ± and settings
 */

import { useState } from 'react';
import {
  Bookmark,
  BookmarkCheck,
  Settings,
  ChevronDown,
  X,
  Minus,
  Plus,
  ALargeSmall,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { SurahListItem, Reciter } from '@/types/quran';
import SurahListDrawer from './SurahListDrawer';

type FontSize = 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge' | 'xxxlarge';

interface QuranControlsProps {
  currentPage: number;
  totalPages: number;
  savedPage?: number | null;
  juz?: number;
  currentSurahNumber?: number;
  surahs: SurahListItem[];
  onSelectSurah: (surahNumber: number) => void;
  onOpenSettings: () => void;
  onBookmark?: () => void;
  onClose: () => void;
  onGoToPage?: (page: number) => void;
  fontSize?: FontSize;
  onFontSizeChange?: (size: FontSize) => void;
  /** Reciter selection */
  reciters?: Reciter[];
  selectedReciterId?: string;
  onReciterChange?: (reciterId: string) => void;
  className?: string;
  /** When false the bar slides off-screen (immersive mode) */
  visible?: boolean;
}

const FONT_SIZE_ORDER: FontSize[] = ['small', 'medium', 'large', 'xlarge', 'xxlarge', 'xxxlarge'];

export function QuranControls({
  currentPage,
  totalPages,
  juz = 1,
  currentSurahNumber,
  surahs,
  onSelectSurah,
  onOpenSettings,
  onBookmark,
  onClose,
  savedPage = null,
  onGoToPage,
  fontSize = 'large',
  onFontSizeChange,
  reciters: _reciters = [],
  selectedReciterId: _selectedReciterId,
  onReciterChange: _onReciterChange,
  className,
  visible = true,
}: QuranControlsProps) {
  const isCurrentPageSaved = savedPage != null && currentPage === savedPage;

  // Find current surah object
  const currentSurah = surahs.find(s => s.number === currentSurahNumber);

  // Drawer state
  const [showSurahList, setShowSurahList] = useState(false);

  // Font size helpers
  const canDecrease = FONT_SIZE_ORDER.indexOf(fontSize) > 0;
  const canIncrease = FONT_SIZE_ORDER.indexOf(fontSize) < FONT_SIZE_ORDER.length - 1;

  const decreaseFontSize = () => {
    const idx = FONT_SIZE_ORDER.indexOf(fontSize);
    if (idx > 0) onFontSizeChange?.(FONT_SIZE_ORDER[idx - 1]);
  };

  const increaseFontSize = () => {
    const idx = FONT_SIZE_ORDER.indexOf(fontSize);
    if (idx < FONT_SIZE_ORDER.length - 1) onFontSizeChange?.(FONT_SIZE_ORDER[idx + 1]);
  };

  return (
    <>
      <div
        className={cn(
          'fixed top-0 left-0 right-0 z-50',
          'bg-background/30 backdrop-blur-xl border-b border-border/50 shadow-lg',
          'transition-all duration-300 ease-in-out',
          !visible && '-translate-y-full opacity-0 pointer-events-none',
          className
        )}
      >
        {/* Row 1: Bookmark + Page Picker + Juz */}
        <div className="flex items-center justify-between px-4 py-2">
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={onBookmark}
              className="rounded-full h-8 w-8"
              title="บันทึกความคืบหน้า"
            >
              <Bookmark
                className={cn(
                  'w-4 h-4',
                  isCurrentPageSaved && 'fill-primary text-primary'
                )}
              />
            </Button>

            {/* Page select */}
            <select
              value={currentPage}
              onChange={e => onGoToPage?.(Number(e.target.value))}
              className={cn(
                'h-7 pl-2 pr-6 rounded-md text-sm font-medium',
                'bg-muted/50 hover:bg-muted border border-border/50',
                'focus:outline-none focus:ring-1 focus:ring-primary/60',
                'cursor-pointer appearance-none',
                'bg-[url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%236b7280\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpolyline points=\'6 9 12 15 18 9\'/%3E%3C/svg%3E")] bg-no-repeat bg-[right_0.4rem_center]',
              )}
              title="เลือกหน้า"
            >
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <option key={p} value={p}>
                  หน้า {p}
                </option>
              ))}
            </select>

            {/* Saved page jump button */}
            {savedPage != null && savedPage !== currentPage && (
              <button
                onClick={() => onGoToPage?.(savedPage)}
                className={cn(
                  'flex items-center gap-1 h-7 pl-1.5 pr-2 rounded-md text-xs font-medium',
                  'bg-primary/10 hover:bg-primary/20 text-primary border border-primary/20',
                  'transition-colors',
                )}
                title={`กลับไปหน้า ${savedPage} (หน้าที่บันทึกไว้)`}
              >
                <BookmarkCheck className="w-3 h-3" />
                {savedPage}
              </button>
            )}
          </div>

          <span className="text-xs text-muted-foreground">
            Juz {juz}
          </span>
        </div>

        {/* Row 2: Surah selector + Font Size ± + Settings */}
        <div className="flex items-center justify-between px-4 pb-2.5">
          {/* Surah Dropdown Trigger */}
          <button
            onClick={() => setShowSurahList(true)}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-lg',
              'text-sm font-medium',
              'bg-muted/50 hover:bg-muted transition-colors',
              'border border-border/50'
            )}
          >
            <span>
              {currentSurah
                ? `${currentSurah.number}. ${currentSurah.englishName}`
                : `เลือกซูเราะฮ์`}
            </span>
            <ChevronDown className="w-3.5 h-3.5 opacity-50" />
          </button>

          {/* Right actions */}
          <div className="flex items-center gap-0.5">
            {/* Font size − */}
            <Button
              variant="ghost"
              size="icon"
              onClick={decreaseFontSize}
              disabled={!canDecrease}
              className="rounded-full h-8 w-8"
              title="ลดขนาดตัวอักษร"
            >
              <Minus className="w-3.5 h-3.5" />
            </Button>

            {/* Font size label */}
            <ALargeSmall className="w-4 h-4 text-muted-foreground" />

            {/* Font size + */}
            <Button
              variant="ghost"
              size="icon"
              onClick={increaseFontSize}
              disabled={!canIncrease}
              className="rounded-full h-8 w-8"
              title="เพิ่มขนาดตัวอักษร"
            >
              <Plus className="w-3.5 h-3.5" />
            </Button>

            <div className="w-px h-4 bg-border/50 mx-0.5" />

            <Button
              variant="ghost"
              size="icon"
              onClick={onOpenSettings}
              className="rounded-full h-8 w-8"
              title="ตั้งค่า"
            >
              <Settings className="w-4 h-4" />
            </Button>
            <Button
              size="icon"
              onClick={onClose}
              className="rounded-full h-8 w-8"
              title="ปิด"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <SurahListDrawer
        open={showSurahList}
        onClose={() => setShowSurahList(false)}
        surahs={surahs}
        onSelectSurah={onSelectSurah}
        currentSurahNumber={currentSurahNumber}
      />
    </>
  );
}

export default QuranControls;
